import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Wand2, Play, Trash2, Loader2, Download, Share2 } from 'lucide-react';
import api, { API } from '@/lib/api';
import { usePlayer } from '@/contexts/PlayerContext';
import { COLOR_META } from '@/lib/colorNoise';
import BreathingCircle from '@/components/BreathingCircle';
import ShareModal from '@/components/ShareModal';
import DownloadButton from '@/components/DownloadButton';

const COLOR_OPTIONS = ['pink', 'brown', 'white', 'green', 'blue', 'violet', 'grey'];
const NATURE_OPTIONS = [
    { v: 'rain',   label: 'Rain' },
    { v: 'waves',  label: 'Ocean' },
    { v: 'forest', label: 'Forest' },
];

const VOICES = [
    { v: 'shimmer', label: 'Shimmer', desc: 'Bright and gentle' },
    { v: 'nova', label: 'Nova', desc: 'Warm and steady' },
    { v: 'echo', label: 'Echo', desc: 'Smooth and calm' },
    { v: 'fable', label: 'Fable', desc: 'Storytelling' },
    { v: 'sage', label: 'Sage', desc: 'Wise, measured' },
    { v: 'coral', label: 'Coral', desc: 'Warm, friendly' },
    { v: 'alloy', label: 'Alloy', desc: 'Neutral, balanced' },
    { v: 'onyx', label: 'Onyx', desc: 'Deep, grounding' },
    { v: 'ash', label: 'Ash', desc: 'Clear, articulate' },
];

const TONES = [
    { v: 'soothing', label: 'Soothing' },
    { v: 'grounding', label: 'Grounding' },
    { v: 'uplifting', label: 'Uplifting' },
    { v: 'sleepy', label: 'Sleepy' },
    { v: 'energizing', label: 'Energizing' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveAudio = (u) => (u?.startsWith('http') ? u : `${BACKEND_URL}${u}`);

export default function Create() {
    const [tab, setTab] = useState('meditation');
    const [library, setLibrary] = useState([]);
    const [share, setShare] = useState(null);
    const { playTrack, setFullscreen } = usePlayer();

    const refreshLibrary = async () => {
        try { const r = await api.get('/ai/library'); setLibrary(r.data || []); } catch {}
    };
    useEffect(() => { refreshLibrary(); }, []);

    const playItem = (i) => {
        playTrack({
            id: i.id, title: i.name, subtitle: `Your ${i.kind === 'ai_meditation' ? 'meditation' : 'visualization'} • ${i.voice}`,
            image: undefined, audio_url: i.audio_url,
            kind: i.kind, duration_minutes: i.duration_minutes,
            metadata: i.metadata || null,
        });
        setFullscreen(true);
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this session? This cannot be undone.')) return;
        await api.delete(`/ai/library/${id}`);
        refreshLibrary();
    };

    return (
        <div className="space-y-8" data-testid="create-page">
            <header>
                <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">Create</p>
                <h1 className="font-serif text-4xl md:text-5xl text-forest mt-2">A session, written for you</h1>
                <p className="text-forest-soft mt-2 max-w-xl">
                    Tell us how you’re feeling, and we’ll write and narrate a meditation you can keep.
                </p>
            </header>

            <div className="qm-card p-2 inline-flex items-center gap-1" data-testid="create-tabs">
                <button
                    onClick={() => setTab('meditation')}
                    data-testid="tab-create-meditation"
                    className={`qm-pill ${tab === 'meditation' ? 'bg-forest text-cream border-forest' : 'border-transparent hover:bg-sage/30'}`}
                >
                    <Sparkles size={14} className="inline mr-1.5 -mt-0.5" /> AI Meditation
                </button>
                <button
                    onClick={() => setTab('visualization')}
                    data-testid="tab-create-visualization"
                    className={`qm-pill ${tab === 'visualization' ? 'bg-forest text-cream border-forest' : 'border-transparent hover:bg-sage/30'}`}
                >
                    <Wand2 size={14} className="inline mr-1.5 -mt-0.5" /> Self-Visualization
                </button>
            </div>

            {tab === 'meditation' ? (
                <MeditationForm onCreated={refreshLibrary} />
            ) : (
                <VisualizationForm onCreated={refreshLibrary} />
            )}

            <section className="space-y-3" data-testid="ai-library-section">
                <h2 className="font-serif text-2xl text-forest">Your private library</h2>
                {library.length === 0 ? (
                    <div className="qm-card p-8 text-center text-forest-soft">
                        Nothing here yet. Make your first session above — we’ll keep it just for you.
                    </div>
                ) : (
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-library-list">
                        {library.map((i) => (
                            <li key={i.id} className="qm-card p-5 flex flex-col gap-3">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-forest-soft">
                                            {i.kind === 'ai_meditation' ? 'Meditation' : 'Visualization'} · {i.voice}
                                        </div>
                                        <div className="font-serif text-lg text-forest mt-1">{i.name}</div>
                                        <div className="text-xs text-forest-soft mt-1">~{i.duration_minutes} min</div>
                                    </div>
                                </div>
                                <p className="text-sm text-forest-soft line-clamp-3 italic">“{i.script.slice(0, 160)}…”</p>
                                <div className="mt-auto flex items-center gap-2 pt-2">
                                    <button onClick={() => playItem(i)} data-testid={`ai-play-${i.id}`} className="qm-btn-primary inline-flex items-center gap-1.5 text-sm py-2 px-4">
                                        <Play size={14} /> Play
                                    </button>
                                    <a
                                        href={resolveAudio(i.audio_url)}
                                        download={`${i.name}.mp3`}
                                        data-testid={`ai-download-${i.id}`}
                                        className="qm-btn-ghost text-sm inline-flex items-center gap-1.5"
                                    >
                                        <Download size={14} /> MP3
                                    </a>
                                    <button
                                        onClick={() => setShare({
                                            card: 'session',
                                            title: i.name,
                                            minutes: i.duration_minutes,
                                            kind: i.kind,
                                        })}
                                        data-testid={`ai-share-${i.id}`}
                                        aria-label={`Share ${i.name}`}
                                        className="rounded-full w-9 h-9 grid place-items-center text-forest hover:bg-sage/30"
                                    >
                                        <Share2 size={14} />
                                    </button>
                                    <DownloadButton
                                        item={{ id: i.id, title: i.name, audio_url: i.audio_url, category: 'AI', kind: i.kind, duration_minutes: i.duration_minutes }}
                                        testIdPrefix="dl-ai"
                                    />
                                    <button onClick={() => remove(i.id)} data-testid={`ai-delete-${i.id}`} className="ml-auto rounded-full w-9 h-9 grid place-items-center text-forest hover:bg-dusty/40">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {share && <ShareModal data={share} onClose={() => setShare(null)} />}
        </div>
    );
}

function GeneratingOverlay({ label }) {
    return (
        <div className="absolute inset-0 grid place-items-center bg-cream/85 backdrop-blur-sm z-10 rounded-3xl" data-testid="generating-overlay">
            <div className="text-center">
                <BreathingCircle size={180} label={label} />
                <p className="text-forest-soft text-sm mt-4 max-w-xs mx-auto">
                    This usually takes 15–40 seconds. Stay with the breath.
                </p>
            </div>
        </div>
    );
}

function MeditationForm({ onCreated }) {
    const [name, setName] = useState('');
    const [focus, setFocus] = useState('');
    const [duration, setDuration] = useState(5);
    const [tone, setTone] = useState('soothing');
    const [voice, setVoice] = useState('shimmer');
    const [bgColor, setBgColor] = useState('brown');
    const [bgNature, setBgNature] = useState('waves');
    const [bgSleep, setBgSleep] = useState(false);
    const [bgEnabled, setBgEnabled] = useState(true);
    const [bgTouched, setBgTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const { playTrack, setFullscreen } = usePlayer();

    // Fetch tone-based BG suggestion. Only overrides the user's selection
    // if they haven't manually touched the BG fields yet.
    useEffect(() => {
        if (bgTouched) return;
        api.get(`/soundscapes/suggest?tone=${tone}`)
            .then((r) => {
                const bg = r.data || {};
                if (bg.color) setBgColor(bg.color);
                setBgNature(bg.nature || '');
                setBgSleep(!!bg.sleep);
            })
            .catch(() => {});
    }, [tone, bgTouched]);

    const buildBackground = () => {
        if (!bgEnabled) return null;
        if (!bgColor && !bgNature) return null;
        return {
            color: bgColor || null,
            nature: bgNature || null,
            volume: 0.3,
            sleep: !!bgSleep,
        };
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(''); setBusy(true);
        try {
            const background_sound = buildBackground();
            const { data } = await api.post('/ai/meditation/generate', {
                name: name.trim(), focus: focus.trim(), duration_minutes: duration, tone, voice,
                background_sound,
            });
            await onCreated();
            playTrack({
                id: data.id, title: data.name, subtitle: `Your meditation • ${data.voice}`,
                audio_url: data.audio_url, kind: 'ai_meditation', duration_minutes: data.duration_minutes,
                metadata: data.metadata || null,
            });
            setFullscreen(true);
            setName(''); setFocus('');
        } catch (err) {
            setError(err?.response?.data?.detail || 'We could not generate this session. Please try again.');
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="qm-card p-7 md:p-9 relative" data-testid="meditation-form">
            {busy && <GeneratingOverlay label="Writing" />}
            <h2 className="font-serif text-2xl text-forest mb-6">Custom meditation</h2>
            <div className="grid md:grid-cols-2 gap-5">
                <div>
                    <label className="text-sm text-forest-soft" htmlFor="med-name">Name this session</label>
                    <input id="med-name" required value={name} onChange={(e) => setName(e.target.value)}
                           placeholder="e.g. Tuesday morning reset"
                           className="qm-input mt-1" data-testid="med-name-input" />
                </div>
                <div>
                    <label className="text-sm text-forest-soft" htmlFor="med-focus">What’s on your mind?</label>
                    <input id="med-focus" required minLength={3} value={focus} onChange={(e) => setFocus(e.target.value)}
                           placeholder="e.g. Easing tension before a meeting"
                           className="qm-input mt-1" data-testid="med-focus-input" />
                </div>
                <div>
                    <label className="text-sm text-forest-soft">Duration</label>
                    <div className="flex flex-wrap gap-2 mt-1" data-testid="med-duration">
                        {[3, 5, 8, 10, 12, 15].map((d) => (
                            <button key={d} type="button" onClick={() => setDuration(d)}
                                    data-testid={`med-duration-${d}`}
                                    className={`qm-pill text-sm ${duration === d ? 'bg-forest text-cream border-forest' : 'border-forest/20 hover:bg-sage/30'}`}>
                                {d} min
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-sm text-forest-soft">Tone</label>
                    <div className="flex flex-wrap gap-2 mt-1" data-testid="med-tone">
                        {TONES.map((t) => (
                            <button key={t.v} type="button" onClick={() => setTone(t.v)}
                                    data-testid={`tone-${t.v}`}
                                    className={`qm-pill text-sm ${tone === t.v ? 'bg-forest text-cream border-forest' : 'border-forest/20 hover:bg-sage/30'}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-5">
                <label className="text-sm text-forest-soft">Voice</label>
                <div className="grid sm:grid-cols-3 gap-2 mt-2" data-testid="med-voice">
                    {VOICES.map((v) => (
                        <button key={v.v} type="button" onClick={() => setVoice(v.v)}
                                data-testid={`voice-${v.v}`}
                                className={`text-left rounded-2xl border p-3 transition ${voice === v.v ? 'border-forest bg-sage/30' : 'border-border hover:bg-sage/15'}`}>
                            <div className="font-medium text-forest">{v.label}</div>
                            <div className="text-xs text-forest-soft">{v.desc}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Background Sound selector */}
            <div className="mt-6 rounded-3xl bg-cream/60 border border-forest/10 p-4" data-testid="med-background">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                        <div className="text-sm font-medium text-forest">Background sound</div>
                        <p className="text-xs text-forest-soft">We&rsquo;ll suggest one for your tone — adjust or turn it off below.</p>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-forest-soft cursor-pointer">
                        <input
                            type="checkbox"
                            checked={bgEnabled}
                            onChange={(e) => { setBgEnabled(e.target.checked); setBgTouched(true); }}
                            data-testid="med-bg-enabled"
                        />
                        {bgEnabled ? 'On' : 'Off'}
                    </label>
                </div>

                {bgEnabled && (
                    <>
                        <div className="text-xs uppercase tracking-[0.15em] text-forest-soft mb-1">Colour</div>
                        <div className="flex flex-wrap gap-2 mb-3" data-testid="med-bg-color-row">
                            <button
                                type="button"
                                onClick={() => { setBgColor(''); setBgTouched(true); }}
                                data-testid="med-bg-color-none"
                                aria-pressed={!bgColor}
                                className={`qm-pill text-sm ${!bgColor ? 'bg-forest text-cream border-forest' : 'border-forest/20 bg-white/60 hover:bg-sage/30'}`}
                            >
                                None
                            </button>
                            {COLOR_OPTIONS.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => { setBgColor(c); setBgTouched(true); }}
                                    data-testid={`med-bg-color-${c}`}
                                    aria-pressed={bgColor === c}
                                    className={`qm-pill text-sm ${bgColor === c ? 'bg-forest text-cream border-forest' : 'border-forest/20 bg-white/60 hover:bg-sage/30'}`}
                                >
                                    {COLOR_META[c].label}
                                </button>
                            ))}
                        </div>

                        <div className="text-xs uppercase tracking-[0.15em] text-forest-soft mb-1">Nature</div>
                        <div className="flex flex-wrap gap-2 mb-3" data-testid="med-bg-nature-row">
                            <button
                                type="button"
                                onClick={() => { setBgNature(''); setBgTouched(true); }}
                                data-testid="med-bg-nature-none"
                                aria-pressed={!bgNature}
                                className={`qm-pill text-sm ${!bgNature ? 'bg-forest text-cream border-forest' : 'border-forest/20 bg-white/60 hover:bg-sage/30'}`}
                            >
                                None
                            </button>
                            {NATURE_OPTIONS.map((n) => (
                                <button
                                    key={n.v}
                                    type="button"
                                    onClick={() => { setBgNature(n.v); setBgTouched(true); }}
                                    data-testid={`med-bg-nature-${n.v}`}
                                    aria-pressed={bgNature === n.v}
                                    className={`qm-pill text-sm ${bgNature === n.v ? 'bg-forest text-cream border-forest' : 'border-forest/20 bg-white/60 hover:bg-sage/30'}`}
                                >
                                    {n.label}
                                </button>
                            ))}
                        </div>

                        <label className="flex items-center gap-2 text-xs text-forest-soft cursor-pointer">
                            <input
                                type="checkbox"
                                checked={bgSleep}
                                onChange={(e) => { setBgSleep(e.target.checked); setBgTouched(true); }}
                                data-testid="med-bg-sleep"
                            />
                            Add gentle sleep pulses to the colour noise
                        </label>
                    </>
                )}
            </div>

            {error && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="med-error">{error}</div>
            )}
            <button type="submit" disabled={busy} data-testid="med-generate-button"
                    className="qm-btn-primary mt-6 inline-flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {busy ? 'Composing…' : 'Generate my meditation'}
            </button>
        </form>
    );
}

function VisualizationForm({ onCreated }) {
    const [mode, setMode] = useState('template');
    const [name, setName] = useState('');
    const [voice, setVoice] = useState('shimmer');
    const [script, setScript] = useState('');
    const [goal, setGoal] = useState('');
    const [setting, setSetting] = useState('');
    const [feeling, setFeeling] = useState('');
    const [affirmation, setAffirmation] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const { playTrack, setFullscreen } = usePlayer();

    const submit = async (e) => {
        e.preventDefault();
        setError(''); setBusy(true);
        try {
            const payload = mode === 'free'
                ? { name: name.trim(), mode, voice, script: script.trim() }
                : { name: name.trim(), mode, voice, goal: goal.trim(), setting: setting.trim(), feeling: feeling.trim(), affirmation: affirmation.trim() };
            const { data } = await api.post('/ai/visualization/generate', payload);
            await onCreated();
            playTrack({
                id: data.id, title: data.name, subtitle: `Your visualization • ${data.voice}`,
                audio_url: data.audio_url, kind: 'ai_visualization', duration_minutes: data.duration_minutes,
            });
            setFullscreen(true);
            setName(''); setScript(''); setGoal(''); setSetting(''); setFeeling(''); setAffirmation('');
        } catch (err) {
            setError(err?.response?.data?.detail || 'We could not generate this session. Please try again.');
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="qm-card p-7 md:p-9 relative" data-testid="visualization-form">
            {busy && <GeneratingOverlay label="Imagining" />}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <h2 className="font-serif text-2xl text-forest">Self-Visualization Studio</h2>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setMode('template')} data-testid="viz-mode-template"
                            className={`qm-pill text-sm ${mode === 'template' ? 'bg-forest text-cream border-forest' : 'border-forest/20 hover:bg-sage/30'}`}>
                        Guided template
                    </button>
                    <button type="button" onClick={() => setMode('free')} data-testid="viz-mode-free"
                            className={`qm-pill text-sm ${mode === 'free' ? 'bg-forest text-cream border-forest' : 'border-forest/20 hover:bg-sage/30'}`}>
                        Write my own
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                    <label className="text-sm text-forest-soft" htmlFor="viz-name">Name this visualization</label>
                    <input id="viz-name" required value={name} onChange={(e) => setName(e.target.value)}
                           placeholder="e.g. The version of me at peace"
                           className="qm-input mt-1" data-testid="viz-name-input" />
                </div>

                {mode === 'template' ? (
                    <>
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="viz-goal">What do you want to visualize?</label>
                            <input id="viz-goal" required value={goal} onChange={(e) => setGoal(e.target.value)}
                                   placeholder="A calm, confident morning"
                                   className="qm-input mt-1" data-testid="viz-goal-input" />
                        </div>
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="viz-setting">Where does this happen?</label>
                            <input id="viz-setting" required value={setting} onChange={(e) => setSetting(e.target.value)}
                                   placeholder="A sunlit kitchen by an open window"
                                   className="qm-input mt-1" data-testid="viz-setting-input" />
                        </div>
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="viz-feeling">What feeling do you want to embody?</label>
                            <input id="viz-feeling" required value={feeling} onChange={(e) => setFeeling(e.target.value)}
                                   placeholder="Quiet confidence"
                                   className="qm-input mt-1" data-testid="viz-feeling-input" />
                        </div>
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="viz-affirm">A short affirmation</label>
                            <input id="viz-affirm" required value={affirmation} onChange={(e) => setAffirmation(e.target.value)}
                                   placeholder="I belong in my own life."
                                   className="qm-input mt-1" data-testid="viz-affirmation-input" />
                        </div>
                    </>
                ) : (
                    <div className="md:col-span-2">
                        <label className="text-sm text-forest-soft" htmlFor="viz-script">Your script</label>
                        <textarea id="viz-script" required minLength={20} rows={8}
                                  value={script} onChange={(e) => setScript(e.target.value)}
                                  placeholder="Write the scene as you’d like to be guided through it…"
                                  className="qm-input mt-1 resize-y" data-testid="viz-script-input" />
                        <p className="text-xs text-forest-soft mt-1">Up to ~3,800 characters — perfect for a 3–5 minute visualization.</p>
                    </div>
                )}
            </div>

            <div className="mt-5">
                <label className="text-sm text-forest-soft">Voice</label>
                <div className="grid sm:grid-cols-3 gap-2 mt-2" data-testid="viz-voice">
                    {VOICES.map((v) => (
                        <button key={v.v} type="button" onClick={() => setVoice(v.v)}
                                data-testid={`viz-voice-${v.v}`}
                                className={`text-left rounded-2xl border p-3 transition ${voice === v.v ? 'border-forest bg-sage/30' : 'border-border hover:bg-sage/15'}`}>
                            <div className="font-medium text-forest">{v.label}</div>
                            <div className="text-xs text-forest-soft">{v.desc}</div>
                        </button>
                    ))}
                </div>
            </div>
            {error && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="viz-error">{error}</div>
            )}
            <button type="submit" disabled={busy} data-testid="viz-generate-button"
                    className="qm-btn-primary mt-6 inline-flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
                {busy ? 'Imagining…' : 'Generate my visualization'}
            </button>
        </form>
    );
}
